import React from 'react';
import {
  MUSICAL_TOKENS,
  normalizeTokenArray,
  VexRhythmGlyph,
  VexStaffFragment,
  pitchToSpanishLabel,
  tokenToLabel,
  BeatCellWorkshopGrid,
  BeatCellStrip,
  buildBarBeatStructure,
  type MusicalToken,
  type RhythmReadingMethod,
} from '@interactive-room';
import { RhythmPatternStrip } from '../print/RhythmPatternStrip';
const TASK_KINDS = [
  { id: 'complete', label: 'Completar figura' },
  { id: 'order', label: 'Ordenar compás' },
  { id: 'identify', label: 'Identificar figura' },
  { id: 'practice', label: 'Practicar patrón' },
] as const;

interface MusicCanvasEditorProps {
  mode: 'rhythm_canvas' | 'melody_staff' | 'ear_training';
  payload: Record<string, unknown>;
  onPayloadChange: (next: Record<string, unknown>) => void;
}

function BuilderInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block text-xs font-semibold uppercase tracking-wide text-slate-400">
      {label}
      <input className="sala-input-dark mt-1 w-full text-sm" value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function RhythmCanvasEditor({ payload, onPayloadChange }: Omit<MusicCanvasEditorProps, 'mode'>) {
  const tokens = normalizeTokenArray(payload.patternTokens as string[]);
  const rhythmMethod = (payload.rhythmMethod as RhythmReadingMethod) || 'takadimi';
  const timeSignature = String(payload.timeSignature || '4/4');
  const beatCells =
    Array.isArray(payload.beatCells) && payload.beatCells.length
      ? payload.beatCells
      : buildBarBeatStructure(tokens, timeSignature as never).beatCells;

  const setTokens = (next: MusicalToken[]) => {
    const structure = buildBarBeatStructure(next, timeSignature as never);
    onPayloadChange({
      ...payload,
      patternTokens: next,
      beatCells: structure.beatCells,
      rhythmicCells: structure.cells,
      beatsPerBar: structure.beatsPerBar,
    });
  };

  return (
    <div className="music-canvas-editor space-y-4">
      <BeatCellStrip
        beatCells={beatCells}
        timeSignature={timeSignature as never}
        targetBarNumber={Number(payload.targetBarNumber) || undefined}
      />
      <BeatCellWorkshopGrid
        beatCells={beatCells}
        timeSignature={timeSignature as never}
        readingMethod={rhythmMethod}
        taskKind={String(payload.taskKind || 'complete') as never}
        blankIndices={
          payload.taskKind === 'transcribe' || payload.taskKind === 'practice'
            ? beatCells.map((_, i) => i)
            : [Math.min(1, beatCells.length - 1)]
        }
        patternTokens={tokens}
        graphicMode={payload.graphicMode as never}
        values={{}}
        disabled
        onSlotChange={() => {}}
      />
      <RhythmPatternStrip
        patternTokens={tokens}
        rhythmMethod={rhythmMethod}
        graphicMode={payload.graphicMode as never}
        slotMode="missing"
        printMode={false}
      />
      <div className="flex flex-wrap gap-2">
        {MUSICAL_TOKENS.map((tok) => (
          <button
            key={tok}
            type="button"
            className="music-token-chip"
            title={tokenToLabel(tok, rhythmMethod)}
            onClick={() => setTokens([...tokens, tok])}
          >
            <VexRhythmGlyph token={tok} size={28} color="#e8e4dc" />
          </button>
        ))}
        <button type="button" className="studio-rail-btn text-[10px]" onClick={() => setTokens(tokens.slice(0, -1))}>
          Quitar última
        </button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-xs text-slate-400">
          Compás
          <select
            className="sala-input-dark mt-1 w-full text-sm"
            value={String(payload.timeSignature || '4/4')}
            onChange={(e) => onPayloadChange({ ...payload, timeSignature: e.target.value })}
          >
            <option value="4/4">4/4</option>
            <option value="3/4">3/4</option>
            <option value="2/4">2/4</option>
          </select>
        </label>
        <label className="text-xs text-slate-400">
          Tipo de ejercicio
          <select
            className="sala-input-dark mt-1 w-full text-sm"
            value={String(payload.taskKind || 'complete')}
            onChange={(e) => onPayloadChange({ ...payload, taskKind: e.target.value })}
          >
            {TASK_KINDS.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <BuilderInput
        label="Instrucción al alumno"
        value={String(payload.prompt || '')}
        onChange={(v) => onPayloadChange({ ...payload, prompt: v })}
      />
    </div>
  );
}

function MelodyStaffEditor({ payload, onPayloadChange }: Omit<MusicCanvasEditorProps, 'mode'>) {
  const notes = Array.isArray(payload.notes) ? payload.notes : [];
  const note0 = (notes[0] as Record<string, unknown>) || { pitch: 'G4', duration: 'Q' };
  const staffNotes = notes.length
    ? notes
    : [{ id: 'n1', pitch: 'G4', duration: 'Q' as MusicalToken }];

  return (
    <div className="music-canvas-editor space-y-4">
      <VexStaffFragment
        notes={staffNotes as never}
        clef={payload.clef === 'bass' ? 'bass' : 'treble'}
        timeSignature={String(payload.timeSignature || '4/4')}
        highlightNoteId={String(payload.highlightNoteId || 'n1')}
        className="music-staff-frame"
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <BuilderInput
          label="Nota (pitch Vex: G4, C5…)"
          value={String(note0.pitch || 'G4')}
          onChange={(v) =>
            onPayloadChange({
              ...payload,
              notes: [{ id: 'n1', pitch: v, duration: note0.duration || 'Q' }],
              highlightNoteId: 'n1',
              answerLabel: pitchToSpanishLabel(v),
            })
          }
        />
        <BuilderInput
          label="Respuesta esperada (español)"
          value={String(payload.answerLabel || '')}
          onChange={(v) => onPayloadChange({ ...payload, answerLabel: v })}
        />
      </div>
      <BuilderInput
        label="Pregunta"
        value={String(payload.prompt || '')}
        onChange={(v) => onPayloadChange({ ...payload, prompt: v })}
      />
    </div>
  );
}

function EarTrainingEditor({ payload, onPayloadChange }: Omit<MusicCanvasEditorProps, 'mode'>) {
  const noteNames = Array.isArray(payload.noteNames) ? payload.noteNames.map(String).join(', ') : '';

  return (
    <div className="music-canvas-editor space-y-4">
      <BuilderInput
        label="Guía docente (dictado en vivo)"
        value={String(payload.teacherScript || '')}
        onChange={(v) => onPayloadChange({ ...payload, teacherScript: v })}
      />
      <BuilderInput
        label="Consigna al alumno"
        value={String(payload.studentPrompt || '')}
        onChange={(v) => onPayloadChange({ ...payload, studentPrompt: v })}
      />
      <BuilderInput
        label="Notas del dictado (Do, Mi, Sol…)"
        value={noteNames}
        onChange={(v) =>
          onPayloadChange({
            ...payload,
            noteNames: v.split(',').map((s) => s.trim()).filter(Boolean),
          })
        }
      />
      <p className="text-[11px] text-slate-500">
        En digital el alumno escucha vía Web Audio; en papel solo aparece la guía docente.
      </p>
    </div>
  );
}

export function MusicCanvasEditor({ mode, payload, onPayloadChange }: MusicCanvasEditorProps) {
  switch (mode) {
    case 'rhythm_canvas':
      return <RhythmCanvasEditor payload={payload} onPayloadChange={onPayloadChange} />;
    case 'melody_staff':
      return <MelodyStaffEditor payload={payload} onPayloadChange={onPayloadChange} />;
    case 'ear_training':
      return <EarTrainingEditor payload={payload} onPayloadChange={onPayloadChange} />;
    default:
      return null;
  }
}

export function isMusicNativeMode(mode: string): mode is 'rhythm_canvas' | 'melody_staff' | 'ear_training' {
  return mode === 'rhythm_canvas' || mode === 'melody_staff' || mode === 'ear_training';
}
